import React, {useState} from "react";
import {SubmitHandler, useForm, useWatch} from "react-hook-form";
import {AddressResType} from "../../../../pages/customer/correspondent/detail/interface";
import {BasicSearchType} from "../../../../types/common/basic";
import {useReadAddressList} from "../../../../pages/customer/correspondent/detail/hooks";
import EmptyTableRow from "../../../../pages/common/EmptyTableRow";
import {ModalInnerConfirmType} from "../interface";
import {useModal} from "../../../../hooks";
import PaginationSub from "../../pagination/PaginationSub";
import {CreateAddress} from "../../../../pages/customer/correspondent/detail/interface";
import {createAddressInit} from "../../../../pages/customer/correspondent/detail/state";
import DaumPost from "../../DaumPost";
import {useSaveAccountAddressInfo} from "../../../../pages/customer/correspondent/detail/hooks/useSaveAccountAddressInfo";
import {useUser} from "../../../../util/userContext/UserContext";
import {useQueryClient} from "@tanstack/react-query";

// 내부수정: 주소 생성 모달 내부 컴포넌트 (AddressTab.tsx의 CreateAddressInner를 복붙, 발송지 찾기 전용으로 사용)
interface CreateAddressInnerProps {
    id: string
    accountName?: string
    modalDepth: number
    afterSaveCallback: () => void
}

interface SearchAddressInnerProps extends ModalInnerConfirmType<AddressResType> {
    hideCreateButton?: boolean // 내부수정: 외부 발주 페이지 등 CRM 미접근 화면에서 주소 생성 버튼 숨김
}

const CreateAddressInner = ({id, accountName, modalDepth, afterSaveCallback}: CreateAddressInnerProps) => {
    const {user} = useUser()
    const {handleModal, resetModal} = useModal();

    const {register, handleSubmit, control, reset, setValue,} = useForm({
        defaultValues: createAddressInit,
    });

    const watchCreateData = {
        ...createAddressInit,
        ...useWatch<CreateAddress>({control})
    }

    const {mutateAsync: mutateAsyncSaveAccountAddressInfo} = useSaveAccountAddressInfo()

    const onCancel = () => {
        reset()
        resetModal(modalDepth)
    }

    const onConfirm = () => {
        const param: CreateAddress = {
            ...watchCreateData,
            _new_l_customer_value: id,
            createdByValue: user ? user.crmId : ""
        }

        mutateAsyncSaveAccountAddressInfo(param).then(res => {
            if (res.data.code === "0000") {
                handleModal({
                    isOpen: true,
                    contents: "저장되었습니다.",
                    type: "alert",
                    closeAction: () => {
                        onCancel()
                        afterSaveCallback()
                    },
                    depth: modalDepth + 1
                })
            }
        })
    }

    return <>
        <table className="table table-bordered my_table table_detail">
            <tbody>
            <tr>
                <th style={{width: "16.66%"}}>관련 거래처</th>
                <td className="p-4px">
                    <input type="text"
                           className="form-control"
                           value={accountName ?? ""}
                           disabled/>
                </td>
            </tr>
            <tr>
                <th style={{width: "16.66%"}}>이름</th>
                <td className="p-4px" colSpan={3}>
                    <input type="text"
                           className="form-control"
                           value={watchCreateData.newName}
                           {...register("newName")}/>
                </td>
            </tr>
            <tr>
                <th>주소</th>
                <td className="p-4px" colSpan={3}>
                    <div className="d-flex">
                        <input type="text"
                               className="form-control w-150px h-34px"
                               placeholder="우편번호"
                               value={watchCreateData.new_txt_postalcode}
                               disabled/>
                        <DaumPost onCompleteCallback={(data) => {
                            setValue("new_txt_postalcode", data.zipcode);
                            setValue("new_txt_line1", data.address);
                        }}/>
                        <input type="text"
                               className="form-control ms-5px h-34px"
                               value={watchCreateData.new_txt_line1}
                               disabled/>
                    </div>
                    <div className="d-flex mt-6px">
                        <input type="text"
                               className="form-control h-34px"
                               placeholder="상세 주소"
                               value={watchCreateData.new_txt_line2}
                               {...register("new_txt_line2")}/>
                    </div>
                </td>
            </tr>
            </tbody>
        </table>

        <div className="mt-3 d-flex align-items-center justify-content-end">
            <button type="button" className="btn btn-outline-primary w-100px h-38px my-0" onClick={onCancel}>취소</button>
            <button type="button" className="btn btn-dark w-100px h-38px my-0 ms-3" onClick={onConfirm}>저장</button>
        </div>
    </>
}

/**
 * 주소 검색 모달
 * @param props
 * @returns
 */
const SearchAddressInner = ({
                                confirmCallback,
                                cancelCallback,
                                modalDepth,
                                args,
                                hideCreateButton // 내부수정
                            }: SearchAddressInnerProps) => {
    const defaultSearchValue = {
        limit: 10,
        pageNo: 1,
        keywordType: "0",
        keyword: "",
        userId: args && args.userId ? args.userId : undefined,
    };

    const {handleModal, resetModal} = useModal();
    const qc = useQueryClient(); // 내부수정

    const [searchOption, setSearchOption] =
        useState<BasicSearchType & { userId: number | undefined }>(defaultSearchValue);

    const [selectItem, setSelectItem] = useState<AddressResType | null>(null);

    const {data} = useReadAddressList({searchData: {...searchOption, stateFlag: true}});

    const {register, handleSubmit} = useForm({
        defaultValues: searchOption,
    });

    const onSubmit: SubmitHandler<BasicSearchType & { userId: number | undefined }> = (submitData) => {
        // 내부수정: 키워드 추가
        // setSearchOption(submitData);
         setSearchOption({...submitData, pageNo: 1, keywordType1: submitData.keyword1 ? "2" : "1",  keywordType2: submitData.keyword2 ? "34" : "1"});
    };

    const onClose = () => {
        resetModal(modalDepth);
    };

    const onConfirm = () => {
        if (selectItem?.new_addressid) {
            confirmCallback(selectItem);
            onClose();
        } else {
            handleModal({
                isOpen: true,
                contents: "주소를 선택해주세요.",
                type: "alert",
                depth: modalDepth + 1,
            });
        }
    };

    const onCancel = () => {
        onClose();
        cancelCallback && cancelCallback();
    };

    // 내부수정: 주소 생성 버튼 클릭
    const btnCreateAddress = () => {
        if (!selectItem) {
            handleModal({
                isOpen: true,
                contents: "먼저 거래처 검색 후 결과에서 아무 행이나 선택하고 주소 생성을 해주세요. \n* 정확한 거래처명 자동선택 / 오타방지",
                type: "alert",
                depth: modalDepth + 1,
            });
            return;
        }

        handleModal({
            isOpen: true,
            contents: (
                <CreateAddressInner
                    id={selectItem._new_l_customer_value}
                    accountName={selectItem.customerName}
                    modalDepth={modalDepth + 1}
                    afterSaveCallback={() => {
                        qc.invalidateQueries({queryKey: ["readAddressList"]})
                    }}
                />
            ),
            type: "",
            title: "주소 생성",
            modalSize: "lg",
            depth: modalDepth + 1,
        });
    };

    return (
        <>
            <form className="d-flex mb-15px" onSubmit={handleSubmit(onSubmit)}>
                <input type="hidden" {...register("keywordType1")} />
                <input
                    type="text"
                    className="form-control w-150px h-34px"
                    placeholder="관련 거래처"
                    {...register("keyword1")}
                />
                <input type="hidden" {...register("keywordType2")} />
                <input
                    type="text"
                    className="form-control w-250px ms-5px h-34px"
                    placeholder="이름 / 생성자"
                    {...register("keyword2")}
                />
                <button type="submit" className="btn btn-info btn_search w-80px ms-5px">
                    <img src={process.env.PUBLIC_URL + "/assets/images/icon_search.png"} alt=""/>
                    조회
                </button>
                {/* 내부수정: 외부 발주 페이지에서는 주소 생성 버튼 숨김 */}
                {!hideCreateButton && (
                    <button type="button" className="btn btn-primary px-0 w-100px ms-auto" onClick={btnCreateAddress}>
                        주소 생성
                    </button>
                )}
            </form>

            <table className="table table-bordered my_table table_modal">
                <thead>
                <tr>
                    {/* 내부수정: 관련 거래처, 컬럼 사이즈 수정 */}
                    <th style={{width: "3%"}}></th>
                    <th style={{width: "14%"}}>관련 거래처</th>  
                    <th style={{width: "14%"}}>이름</th>
                    <th style={{width: "6%"}}>우편번호</th>
                    <th>주소1</th>
                    <th style={{width: "10%"}}>주소2</th>
                    <th style={{width: "12%"}}>생성자</th>
                </tr>
                </thead>
                <tbody>
                {(data?.addressList ?? [])?.length === 0 ? (
                    <EmptyTableRow/>
                ) : (
                    (data?.addressList ?? [])?.map((item: AddressResType) => {
                        return (
                            <tr className={`clickable ${item.new_addressid === selectItem?.new_addressid ? "lightblue" : ""}`}
                                onClick={() => setSelectItem(item)}>
                                <td>
                                    <div className="checkbox_outer">
                                        <input type="radio"
                                               className="form-check-input"
                                               checked={item.new_addressid === selectItem?.new_addressid}
                                               onClick={() => setSelectItem(item)}
                                               readOnly/>
                                    </div>
                                </td>
                                <td className="text-center">{item?.customerName ?? "-"}</td>
                                <td className="text-center">{item?.newName ?? "-"}</td>
                                <td className="text-center">
                                    {item?.new_txt_postalcode ?? "-"}
                                </td>
                                <td className="text-center">{item?.new_txt_line1 ?? "-"}</td>
                                <td className="text-center">{item?.new_txt_line2 ?? "-"}</td>
                                <td className="text-center">{item?.createName ?? "-"}</td>
                            </tr>
                        );
                    })
                )}
                </tbody>
            </table>

            <PaginationSub
                totalCount={data?.totalCount ?? 0}
                currentPage={Number(searchOption.pageNo)}
                onChangeCallback={(i) =>
                    setSearchOption({...searchOption, pageNo: i})
                }
            />

            <div className="mt-3 d-flex align-items-center justify-content-end">
                <button
                    type="button"
                    className="btn btn-outline-primary w-100px h-38px my-0"
                    onClick={onCancel}
                >
                    취소
                </button>
                <button
                    type="button"
                    className="btn btn-dark w-100px h-38px my-0 ms-3"
                    onClick={onConfirm}
                >
                    선택
                </button>
            </div>
        </>
    );
};

export default SearchAddressInner;
